import { useContext, useEffect, useState } from 'react';
import css from './DocumentsView.module.scss';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import Skeleton from '../../widgets/plug/Skeleton/Skeleton';
import { useWindowDimensions } from '../../common/utils/windowDimensions';
import { skeleton } from './helpers';
import { API_SERVER_URL, getCountryIdByDomain } from '../../common/utils/utils';
import { getData } from '../../common/utils/router';
import FormRadioSwitcher from '../../widgets/form/components/form-radio/FormRadioSwitcher/FormRadioSwitcher';
import T from '../../common/components/T';
import { svgSprite } from '../../hybrid/wrappers';
import SingleButton from '../../widgets/form/form-control-buttons/SingleButton/SingleButton';
import Notification from '../../widgets/notification/Notification';
import moment from 'moment';

const DocumentCard = ({ document: fileDocument, userToken }) => {
    const handleDownload = (pdfDocument) => {
        if (pdfDocument.file?.link.toLowerCase().endsWith('.pdf')) {
            const link = document.createElement('a');
            link.href = `/api/pdf/proxy?url=${pdfDocument.file?.link}&filename=${(pdfDocument.name || `file_${pdfDocument.id}`)
                ?.split(' ')
                .join('_')}.pdf`;

            link.download = 'test.pdf';
            link.target = '_blank';
            link.click();
        } else {
            fetch(pdfDocument.file?.link, {
                headers: {
                    Authorization: `Bearer ${userToken}`
                }
            })
                .then(async (res) => {
                    const blob = await res.blob();

                    const disposition = res.headers.get('content-disposition');
                    let filename = 'file.pdf';

                    if (disposition && disposition.includes('filename=')) {
                        filename = disposition.split('filename=')[1].replace(/["']/g, '');
                    }

                    return { blob, filename };
                })
                .then(({ blob, filename }) => {
                    const url = URL.createObjectURL(blob);

                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;

                    document.body.appendChild(a);
                    a.click();

                    a.remove();
                    URL.revokeObjectURL(url);
                });
        }
    };

    const handleOpen = (pdfDocument) => {
        const link = document.createElement('a');

        if (pdfDocument.file?.link.toLowerCase().endsWith('.pdf')) {
            link.href = pdfDocument.file?.link;
            link.target = '_blank';
            link.click();
        } else {
            fetch(pdfDocument.file?.link, {
                headers: {
                    Authorization: `Bearer ${userToken}`
                }
            })
                .then((res) => {
                    return res?.blob();
                })
                .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    window.open(url);
                });
        }
    };

    return (
        <div className={css.document}>
            <div className={css.documentIcon}>{svgSprite('document-circle-fill')}</div>
            <p className={css.documentTitle}>{fileDocument.name}</p>
            {fileDocument?.signed && fileDocument.created_at ? (
                <p className={css.documentTitleDate}>
                    <T
                        textName='signed_date'
                        vars={{ date: moment(fileDocument.created_at).format('DD.MM.YYYY') }}
                        defaultStr='Подписан: ${date}'
                        page='/documents'
                    />
                </p>
            ) : null}
            <div className={css.documentButtons}>
                {!fileDocument?.signed ? (
                    <SingleButton
                        type='cancel'
                        buttonText={<T textName='download' defaultStr='Скачать' page='/documents' />}
                        customButton={{
                            singleButton: css.documentButton
                        }}
                        clickFunc={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDownload(fileDocument);
                        }}
                    />
                ) : (
                    <div className={css.signedLabel}>
                        <T textName='signed_doc' defaultStr='Подписано' page='/documents' />
                    </div>
                )}
                <SingleButton
                    type='ok'
                    buttonText={<T textName='open' defaultStr='Открыть' page='/documents' />}
                    customButton={{
                        singleButton: css.documentButton
                    }}
                    clickFunc={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpen(fileDocument);
                    }}
                />
            </div>
        </div>
    );
};

const RenderData = ({ documents, userToken }) => {
    if (!documents?.length) {
        return (
            <div className={css.emptyDocuments}>
                <Notification
                    icon='warning-rhombus'
                    title={
                        <>
                            <T textName='empty_documents_text' defaultStr='К сожалению документы отсутствуют' page='/documents' />
                        </>
                    }
                    customCss={{
                        icon: css.notifyIcon,
                        notification: css.notification
                    }}
                />
            </div>
        );
    }

    return (
        <div className={css.documents}>
            {documents?.map((document) => {
                return <DocumentCard key={document.id} document={document} userToken={userToken} />;
            })}
        </div>
    );
};

const DocumentsView = ({ setBreadcrumbs }) => {
    const { userToken, lang, localization } = useContext(UserContext);
    const { profileData, loaded } = useContext(UserProfileContext);
    const [documents, setDocuments] = useState(null);
    const width = useWindowDimensions(true);
    const [url, setUrl] = useState(null);

    const topLevelDomain = typeof window !== 'undefined' && window.location.host.split('.').pop();

    useEffect(() => {
        if (loaded) {
            const documentsUrl = new URL(`${API_SERVER_URL}/api/v1/${userToken ? 'documents' : 'documents/anonymous'}`);
            let countryId = '';
            if (userToken) {
                countryId = profileData?.country?.geoname_id;
            } else {
                if (topLevelDomain === 'com' || topLevelDomain === 'cloud') {
                    countryId = localization?.country?.id || localization?.country?.geoname_id;
                } else {
                    countryId = getCountryIdByDomain(topLevelDomain);
                }
            }
            if (!countryId) return;
            // documentsUrl.searchParams.set('filter[type]', type);

            documentsUrl.searchParams.set('filter[country_id]', countryId);
            documentsUrl.searchParams.set('page[size]', 999);

            getData(documentsUrl.href, userToken, lang).then((res) => {
                if (!userToken) {
                    setDocuments(res?.data?.filter((doc) => doc.type == 'certificate'));
                } else {
                    setDocuments(res?.data?.filter((doc) => doc.type !== 'certificate'));
                }
                setUrl(documentsUrl);
            });
        }
    }, [loaded, userToken, lang, localization, topLevelDomain, profileData?.country?.geoname_id]);

    const switcherHandler = (value) => {
        setBreadcrumbs((state) => {
            let lastBreadcrumb = '';
            if (value === 1) {
                lastBreadcrumb = {
                    title: <T textName='certificates' defaultStr='Сертификаты' page='/documents' />
                };
            } else {
                lastBreadcrumb = {
                    title: <T textName='contracts' defaultStr='Договора' page='/documents' />
                };
            }
            return [...state?.slice(0, -1), lastBreadcrumb];
        });
        setDocuments(null);
        let type = '';

        if (value === 1 || !userToken) {
            type = 'certificate';
        } else {
            type = '';
        }
        const searchUrl = url;
        if (type) {
            searchUrl.searchParams.set('filter[type]', type);
        } else {
            searchUrl.searchParams.delete('filter[type]');
        }
        getData(searchUrl.href, userToken, lang).then((res) => {
            setDocuments(res?.data?.filter(type ? () => true : (doc) => doc.type !== 'certificate'));
            setUrl(searchUrl);
        });
    };
    return (
        <div className={css.container}>
            <FormRadioSwitcher
                options={[
                    ...(userToken
                        ? [
                              {
                                  id: 'contracts',
                                  title: <T textName='contracts' defaultStr='Договора' page='/documents' />
                              }
                          ]
                        : []),
                    {
                        id: 'certificate',
                        title: <T textName='certificates' defaultStr='Сертификаты' page='/documents' />
                    }
                ]}
                value={0}
                hardChange={true}
                setFormData={(attr, v) => {
                    switcherHandler(v);
                }}
            />
            <Skeleton
                wait={!!documents}
                Component={RenderData}
                componentProps={{ documents, userToken }}
                elements={skeleton(width)}
            />
        </div>
    );
};

export default DocumentsView;
